import { getArticleCode } from "./articleCode";
import {
  getLineDivisionSlug,
  validateModifyAcceptQuantity,
} from "./quantityValidation";
import { parseSellingPriceTpInput } from "./purchaseOrderLinePricing";

export const MODIFY_ACCEPT_BULK_SAMPLE_CSV = `article_code,quantity,selling_price_tp
2400136,5,
2400137,,25.50
2400138,10,30.00
2400139,,100
`;

export const MODIFY_ACCEPT_BULK_SAMPLE_FILENAME =
  "modify-accept-bulk-sample.csv";

export const normCode = (code) =>
  String(code || "")
    .trim()
    .toLowerCase();

export function isIntegerQtyToken(str) {
  return /^\d+$/.test(String(str || "").trim());
}

function normalizeHeaderToken(token) {
  return String(token || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

export function isBulkHeaderLine(line) {
  const lower = String(line || "").toLowerCase();
  const hasCode =
    lower.includes("article") ||
    lower.includes("sku") ||
    /\bcode\b/.test(lower);
  const hasQty = lower.includes("qty") || lower.includes("quantity");
  const hasPrice = lower.includes("price") || lower.includes("unit_price");
  return hasCode && (hasQty || hasPrice);
}

export function parseHeaderColumns(headerParts) {
  let codeIdx = -1;
  let qtyIdx = -1;
  let priceIdx = -1;

  headerParts.forEach((part, index) => {
    const h = normalizeHeaderToken(part);
    if (
      codeIdx < 0 &&
      (h === "article_code" ||
        h === "article" ||
        h === "sku" ||
        h === "code")
    ) {
      codeIdx = index;
      return;
    }
    if (qtyIdx < 0 && (h === "quantity" || h === "qty")) {
      qtyIdx = index;
      return;
    }
    if (
      priceIdx < 0 &&
      (h === "unit_price" ||
        h === "price" ||
        h === "unitprice" ||
        h === "selling_price_tp" ||
        h === "selling_price" ||
        h === "tp" ||
        h === "cost" ||
        h === "cost_price")
    ) {
      priceIdx = index;
    }
  });

  if (codeIdx < 0 || (qtyIdx < 0 && priceIdx < 0)) {
    return null;
  }

  return { codeIdx, qtyIdx, priceIdx };
}

function parseExplicitFields(codeRaw, qtyStr, priceStr, lineNo) {
  if (!codeRaw) {
    return { error: `Line ${lineNo}: missing article code` };
  }

  const entry = { codeRaw, codeNorm: normCode(codeRaw) };
  let hasAny = false;

  if (qtyStr) {
    const n = Number(qtyStr);
    if (!Number.isFinite(n) || n < 0) {
      return {
        error: `Line ${lineNo}: quantity must be a non-negative number for "${codeRaw}"`,
      };
    }
    entry.qty = Math.round(n * 100) / 100;
    hasAny = true;
  }

  if (priceStr) {
    const priceParsed = parseSellingPriceTpInput(priceStr);
    if (!priceParsed.ok) {
      return {
        error: `Line ${lineNo}: ${priceParsed.error} for "${codeRaw}"`,
      };
    }
    entry.unitPrice = priceParsed.value;
    hasAny = true;
  }

  if (!hasAny) {
    return {
      error: `Line ${lineNo}: at least one of quantity or Selling Price is required for "${codeRaw}"`,
    };
  }

  return { entry };
}

/**
 * Parse one bulk line from article code + value field(s) (non-header format).
 */
export function parseBulkLine(codeRaw, valueFields, lineNo) {
  if (!codeRaw) {
    return { error: `Line ${lineNo}: missing article code before the comma` };
  }

  if (!valueFields.length) {
    return {
      error: `Line ${lineNo}: missing quantity or Selling Price after the comma for "${codeRaw}"`,
    };
  }

  if (valueFields.length > 2) {
    return { error: `Line ${lineNo}: too many fields for "${codeRaw}"` };
  }

  if (valueFields.length === 1) {
    const val = valueFields[0];
    if (!val) {
      return {
        error: `Line ${lineNo}: missing quantity or Selling Price for "${codeRaw}"`,
      };
    }

    const entry = { codeRaw, codeNorm: normCode(codeRaw) };

    if (isIntegerQtyToken(val)) {
      entry.qty = parseInt(val, 10);
      return { entry };
    }

    const priceParsed = parseSellingPriceTpInput(val);
    if (!priceParsed.ok) {
      return {
        error: `Line ${lineNo}: ${priceParsed.error} for "${codeRaw}"`,
      };
    }
    entry.unitPrice = priceParsed.value;
    return { entry };
  }

  const [qtyStr, priceStr] = valueFields;
  return parseExplicitFields(codeRaw, qtyStr, priceStr, lineNo);
}

/**
 * Parse bulk text for Modify & Accept (qty, Selling Price, or both per line).
 * @returns {{ entries: Array<{ codeRaw: string, codeNorm: string, qty?: number, unitPrice?: number }>, errors: string[] }}
 */
export function parseModifyAcceptBulkText(text) {
  const errors = [];
  const entries = [];
  const lines = String(text || "").split(/\r?\n/);

  let headerColMap = null;
  let dataLineNo = 0;

  for (let i = 0; i < lines.length; i += 1) {
    const rawLine = lines[i];
    const line = rawLine.trim();
    if (!line || line.startsWith("#")) continue;

    const parts = line.split(",").map((s) => s.trim());

    if (!headerColMap && isBulkHeaderLine(line)) {
      headerColMap = parseHeaderColumns(parts);
      if (!headerColMap) {
        errors.push(`Line ${i + 1}: unrecognized CSV header`);
      }
      continue;
    }

    dataLineNo += 1;
    const lineNo = dataLineNo;

    let result;
    if (headerColMap) {
      const codeRaw = (parts[headerColMap.codeIdx] ?? "").trim();
      const qtyStr =
        headerColMap.qtyIdx >= 0
          ? (parts[headerColMap.qtyIdx] ?? "").trim()
          : "";
      const priceStr =
        headerColMap.priceIdx >= 0
          ? (parts[headerColMap.priceIdx] ?? "").trim()
          : "";
      result = parseExplicitFields(codeRaw, qtyStr, priceStr, lineNo);
    } else {
      if (line.indexOf(",") === -1) {
        errors.push(
          `Line ${lineNo}: use a comma between article code and quantity or Selling Price (e.g. 2400136, 5). Got: "${line}"`,
        );
        continue;
      }
      result = parseBulkLine(parts[0] ?? "", parts.slice(1), lineNo);
    }

    if (result.error) {
      errors.push(result.error);
    } else if (result.entry) {
      entries.push(result.entry);
    }
  }

  return { entries, errors };
}

/**
 * Merge parsed bulk entries into Modify & Accept quantity/price maps.
 * @returns {{ quantities: Object, unitPrices: Object, unmatched: string[], validationErrors: string[] }}
 */
export function mergeBulkEntriesIntoLineState(
  lineItems,
  quantities,
  unitPrices,
  entries,
) {
  const codeToEntry = new Map();
  const lastRawByNorm = new Map();

  for (const e of entries || []) {
    const prev = codeToEntry.get(e.codeNorm) || {
      codeRaw: e.codeRaw,
      codeNorm: e.codeNorm,
    };
    codeToEntry.set(e.codeNorm, {
      ...prev,
      codeRaw: e.codeRaw,
      ...(e.qty !== undefined ? { qty: e.qty } : {}),
      ...(e.unitPrice !== undefined ? { unitPrice: e.unitPrice } : {}),
    });
    lastRawByNorm.set(e.codeNorm, e.codeRaw);
  }

  const unmatched = [];
  for (const norm of codeToEntry.keys()) {
    const hasLine = (lineItems || []).some(
      (li) => normCode(getArticleCode(li)) === norm,
    );
    if (!hasLine) unmatched.push(lastRawByNorm.get(norm) || norm);
  }

  const nextQuantities = { ...quantities };
  const nextUnitPrices = { ...unitPrices };
  const validationErrors = [];
  for (const li of lineItems || []) {
    const id = li.id || li.order_line_item_id;
    if (!id) continue;
    const norm = normCode(getArticleCode(li));
    const entry = codeToEntry.get(norm);
    const codeLabel = getArticleCode(li) || norm;
    const divisionSlug = getLineDivisionSlug(li);
    if (entry?.qty !== undefined) {
      const parsed = validateModifyAcceptQuantity(entry.qty, divisionSlug, {
        allowZero: true,
      });
      if (!parsed.ok) {
        validationErrors.push(`${codeLabel}: ${parsed.error}`);
      } else {
        nextQuantities[id] = parsed.value;
      }
    }
    if (entry?.unitPrice !== undefined) {
      nextUnitPrices[id] = entry.unitPrice;
    }
  }

  return {
    quantities: nextQuantities,
    unitPrices: nextUnitPrices,
    unmatched,
    validationErrors,
  };
}
